import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const AuthContext = createContext(null);

// ── Axios interceptor: attach token from localStorage ──
axios.interceptors.request.use((config) => {
  const token = localStorage.getItem('session_token');
  if (token) {
    config.headers['Authorization'] = `Bearer ${token}`;
  }
  return config;
});

// ── Axios interceptor: surface admin inactivity timeout with a friendly toast ──
let _inactivityToastShown = false;
axios.interceptors.response.use(
  (res) => res,
  (err) => {
    const detail = err?.response?.data?.detail || '';
    if (err?.response?.status === 401 && typeof detail === 'string' && detail.toLowerCase().includes('inactivity')) {
      if (!_inactivityToastShown) {
        _inactivityToastShown = true;
        toast.error('Signed out due to inactivity', {
          description: detail,
          duration: 6000,
        });
        setTimeout(() => { _inactivityToastShown = false; }, 8000);
      }
      localStorage.removeItem('session_token');
      // Redirect admins back to home so they can re-login
      if (window.location.pathname.startsWith('/admin')) {
        setTimeout(() => { window.location.href = '/'; }, 400);
      }
    }
    return Promise.reject(err);
  }
);

// ── Helper: save/clear token ──
const saveToken = (token) => { if (token) localStorage.setItem('session_token', token); };
const clearToken = () => { localStorage.removeItem('session_token'); };

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [cartCount, setCartCount] = useState(0);
  const [ordersCount, setOrdersCount] = useState(0);
  const [messagesCount, setMessagesCount] = useState(0);

  const fetchCartCount = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/cart/count`);
      setCartCount(res.data.count);
    } catch {
      setCartCount(0);
    }
  }, []);

  const fetchOrdersCount = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/orders/active-count`);
      setOrdersCount(res.data.count);
    } catch {
      setOrdersCount(0);
    }
  }, []);

  const fetchMessagesCount = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/messages/unread-count`);
      setMessagesCount(res.data.count);
    } catch {
      setMessagesCount(0);
    }
  }, []);

  const checkAuth = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/auth/me`);
      setUser(res.data);
      fetchCartCount();
      fetchOrdersCount();
      fetchMessagesCount();
    } catch (err) {
      // On a 401, give the network/cookies one more chance before nuking the
      // session — mobile browsers (esp. Android Chrome) sometimes drop the
      // session cookie on the first request after a cross-origin Back from a
      // payment gateway. The Bearer token in localStorage is still good, so a
      // brief retry usually succeeds. Only a SECOND 401 should clear state.
      if (err.response?.status === 401) {
        try {
          await new Promise((r) => setTimeout(r, 600));
          const retry = await axios.get(`${API}/auth/me`);
          setUser(retry.data);
          fetchCartCount();
          fetchOrdersCount();
          fetchMessagesCount();
        } catch (retryErr) {
          if (retryErr.response?.status === 401) {
            clearToken();
            setUser(null);
            setCartCount(0);
            setOrdersCount(0);
            setMessagesCount(0);
          }
          // Network/5xx on retry: keep the token, leave user as-is.
        }
      }
      // Network errors (no response) keep the existing state untouched.
    } finally {
      setLoading(false);
    }
  }, [fetchCartCount, fetchOrdersCount, fetchMessagesCount]);

  useEffect(() => {
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  const login = async (email, password) => {
    const res = await axios.post(`${API}/auth/login`, { email, password });
    if (res.data.status === 'verify_email') return res.data;
    saveToken(res.data.session_token);
    setUser(res.data);
    fetchCartCount();
    fetchOrdersCount();
    fetchMessagesCount();
    return res.data;
  };

  const register = async (email, password, name) => {
    const res = await axios.post(`${API}/auth/register`, { email, password, name });
    if (res.data.status === 'verify_email') return res.data;
    saveToken(res.data.session_token);
    setUser(res.data);
    return res.data;
  };

  const verifyEmail = async (email, code) => {
    const res = await axios.post(`${API}/auth/verify-email`, { email, code });
    saveToken(res.data.session_token);
    setUser(res.data);
    return res.data;
  };

  const resendCode = async (email) => {
    const res = await axios.post(`${API}/auth/resend-code`, { email });
    return res.data;
  };

  const loginWithGoogle = () => {
    if (!sessionStorage.getItem('auth_return_url')) {
      sessionStorage.setItem('auth_return_url', window.location.pathname + window.location.search);
    }
    const redirectUrl = window.location.origin + '/';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const processGoogleSession = async (sessionId) => {
    const res = await axios.get(`${API}/auth/session?session_id=${sessionId}`);
    saveToken(res.data.session_token);
    setUser(res.data);
    fetchCartCount();
    fetchOrdersCount();
    fetchMessagesCount();
    return res.data;
  };

  const forgotPassword = async (email) => {
    const res = await axios.post(`${API}/auth/forgot-password`, { email });
    return res.data;
  };

  const verifyCode = async (email, code) => {
    const res = await axios.post(`${API}/auth/verify-code`, { email, code });
    saveToken(res.data.session_token);
    setUser(res.data);
    fetchCartCount();
    fetchOrdersCount();
    fetchMessagesCount();
    return res.data;
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {});
    } catch { /* ignore */ }
    clearToken();
    setUser(null);
    setCartCount(0);
    setOrdersCount(0);
    setMessagesCount(0);
  };

  const deleteAccount = async () => {
    await axios.delete(`${API}/auth/account`);
    clearToken();
    setUser(null);
    setCartCount(0);
    setOrdersCount(0);
    setMessagesCount(0);
  };

  const refreshUser = async () => { await checkAuth(); };

  return (
    <AuthContext.Provider value={{
      user, loading, login, register, loginWithGoogle,
      processGoogleSession, logout, refreshUser, isAuthenticated: !!user,
      forgotPassword, verifyCode, verifyEmail, resendCode, deleteAccount,
      cartCount, fetchCartCount,
      ordersCount, fetchOrdersCount,
      messagesCount, fetchMessagesCount,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
